import { createContext, use, useLayoutEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAccountType } from '@/hooks/useAccountType'
import { useSidebarPinCapacity } from '@/hooks/useSidebarPinCapacity'
import { useNavigationPreferences } from '@/hooks/useUserPreferences'
import { normalizeRecordNavHref } from '@/lib/navigation/recordNavHref'
import { useOrganizationStore } from '@/store/organization.store'
import {
  DotsThreeIcon,
  PushPinIcon,
  PushPinSlashIcon,
} from '@phosphor-icons/react'
import { useLocation } from 'react-router-dom'

/**
 * Provided by ModuleLayout around the header actions. Carries the page label
 * for pinning, and lets a page that renders its own overflow ("...") menu
 * suppress the auto-injected one so headers never show two.
 */
export const NavPinContext = createContext<{
  label: string
  setAutoMenuSuppressed: (suppressed: boolean) => void
} | null>(null)

function usePinCurrentPage() {
  const location = useLocation()
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const { isPersonal } = useAccountType()
  const { pinNavItem, unpinNavItem, isNavRoutePinned } =
    useNavigationPreferences()
  const pinCapacity = useSidebarPinCapacity()

  // Store the org-relative route ('/{slug}/tasks' → '/tasks') so pins match
  // the sidebar's nav-item href convention.
  const slug = currentOrganization?.slug
  const href =
    slug && location.pathname.startsWith(`/${slug}/`)
      ? location.pathname.slice(slug.length + 1)
      : location.pathname

  return {
    // A record pinned from one of its tabs should still reopen on the record
    // itself, so collapse record routes to their root before they become pins.
    currentHref: normalizeRecordNavHref(href),
    isNavRoutePinned,
    pinNavItem,
    unpinNavItem,
    pinCapacity,
    // The personal shell has its own navigation without a Pinned section.
    isAvailable: !isPersonal,
  }
}

/**
 * "Pin to sidebar" when the sidebar already holds the maximum number of
 * pins: disabled, with the way out spelled under it.
 */
function PinLimitReachedMenuItem({ max }: { max: number }) {
  return (
    <DropdownMenuItem disabled className="flex-col items-start gap-0.5">
      <span className="flex items-center">
        <PushPinIcon className="mr-2 h-4 w-4" />
        Pin to sidebar
      </span>
      <span className="text-[11px] text-muted-foreground">
        Sidebar is full ({max} pins). Unpin one to add more.
      </span>
    </DropdownMenuItem>
  )
}

/**
 * "Pin to sidebar" as a plain menu item, for pages that already have their
 * own overflow ("...") menu. Pair with <SuppressAutoNavPinMenu /> so the
 * header doesn't also render the standalone menu.
 *
 * The pin label comes from the surrounding ModuleLayout by default; record
 * menus outside that context (or wanting the record's name) pass `label`
 * explicitly. The pin targets the current route unless `href` names the
 * record's canonical org-relative route (useful in split views, where the
 * visible URL may not be the record's own). `withSeparator` renders a menu
 * separator alongside — only when the item itself renders — so callers don't
 * end up with a dangling divider when pinning is unavailable.
 */
export function PinToSidebarMenuItem({
  label,
  href,
  withSeparator,
  visual,
}: {
  label?: string
  href?: string
  withSeparator?: 'before' | 'after'
  /** Record visuals for the pinned row: an uploaded logo, or a color rendered as an initial swatch. */
  visual?: { logoUrl?: string | null; color?: string | null }
} = {}) {
  const context = use(NavPinContext)
  const {
    currentHref,
    isNavRoutePinned,
    pinNavItem,
    unpinNavItem,
    pinCapacity,
    isAvailable,
  } = usePinCurrentPage()
  const effectiveLabel = label ?? context?.label
  const targetHref = href ? normalizeRecordNavHref(href) : currentHref

  if (!effectiveLabel || !isAvailable) {
    return null
  }

  const item = isNavRoutePinned(targetHref) ? (
    <DropdownMenuItem
      className="cursor-pointer"
      onClick={() => void unpinNavItem(targetHref)}
    >
      <PushPinSlashIcon className="mr-2 h-4 w-4" />
      Unpin from sidebar
    </DropdownMenuItem>
  ) : pinCapacity.isFull ? (
    <PinLimitReachedMenuItem max={pinCapacity.max} />
  ) : (
    <DropdownMenuItem
      className="cursor-pointer"
      onClick={() =>
        void pinNavItem({
          href: targetHref,
          label: effectiveLabel,
          logoUrl: visual?.logoUrl ?? undefined,
          color: visual?.color ?? undefined,
        })
      }
    >
      <PushPinIcon className="mr-2 h-4 w-4" />
      Pin to sidebar
    </DropdownMenuItem>
  )

  return (
    <>
      {withSeparator === 'before' && <DropdownMenuSeparator />}
      {item}
      {withSeparator === 'after' && <DropdownMenuSeparator />}
    </>
  )
}

/**
 * Renders nothing; while mounted it tells ModuleLayout to skip the
 * auto-injected "..." menu. Mount it next to a page's own overflow menu
 * (outside the dropdown content, which only mounts when open).
 */
export function SuppressAutoNavPinMenu() {
  const context = use(NavPinContext)
  const setAutoMenuSuppressed = context?.setAutoMenuSuppressed

  useLayoutEffect(() => {
    if (!setAutoMenuSuppressed) return
    setAutoMenuSuppressed(true)
    return () => setAutoMenuSuppressed(false)
  }, [setAutoMenuSuppressed])

  return null
}

/**
 * Standalone page overflow ("...") menu with the "Pin to sidebar" action,
 * auto-rendered by ModuleLayout for pages that don't bring their own menu.
 */
export function NavPinMenu({ label }: { label: string }) {
  const {
    currentHref,
    isNavRoutePinned,
    pinNavItem,
    unpinNavItem,
    pinCapacity,
    isAvailable,
  } = usePinCurrentPage()

  if (!isAvailable) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Same chrome as every module's own ⋯ menu (LayoutActions), so a
            header's overflow reads the same with or without page actions. */}
        <Button
          variant="outline"
          size="icon"
          className="border-border/50 shadow-sm hover:border-border"
          aria-label="Page options"
        >
          <DotsThreeIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isNavRoutePinned(currentHref) ? (
          <DropdownMenuItem onClick={() => void unpinNavItem(currentHref)}>
            <PushPinSlashIcon className="mr-2 h-4 w-4" />
            Unpin from sidebar
          </DropdownMenuItem>
        ) : pinCapacity.isFull ? (
          <PinLimitReachedMenuItem max={pinCapacity.max} />
        ) : (
          <DropdownMenuItem
            onClick={() => void pinNavItem({ href: currentHref, label })}
          >
            <PushPinIcon className="mr-2 h-4 w-4" />
            Pin to sidebar
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
